import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import "./index.css";
import Toolbar from "./components/Toolbar";
import SelectionBar from "./components/SelectionBar";
import FileItem from "./components/FileItem";
import ContextMenu from "./components/ContextMenu";
import NewFolderDialog from "./components/NewFolderDialog";
import MoveCopyDialog from "./components/MoveCopyDialog";
import TrashPanel from "./components/TrashPanel";
import notify from "./utils/notify";
import {
  getUsernameFromToken,
  listFiles,
  listAllFolders,
  uploadFiles,
  createFolder,
  copyFiles,
  renameFile,
  bulkAction,
  viewUrl,
} from "./api";

const DND_MIME = "application/x-fm-items";

function FileManager() {
  const token = localStorage.getItem("token");
  const username = token ? getUsernameFromToken(token) : null;
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [allFolders, setAllFolders] = useState([]);

  const [selected, setSelected] = useState(new Set());
  const [lastClickIndex, setLastClickIndex] = useState(null);
  const [focusIndex, setFocusIndex] = useState(-1);

  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const [viewMode, setViewMode] = useState(() => localStorage.getItem("fm-view-mode") || "grid");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");

  const [uploadProgress, setUploadProgress] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [moveCopyDialog, setMoveCopyDialog] = useState(null);
  const [dropTargetPath, setDropTargetPath] = useState(null);

  const containerRef = useRef(null);

  const relPathOf = useCallback(
    (item) => (currentPath ? `${currentPath}/${item.name}` : item.name),
    [currentPath]
  );

  /* ───────── Data loading ───────── */
  const refreshFiles = useCallback(async () => {
    if (!token) return navigate("/login");
    try {
      const { files } = await listFiles(token, currentPath);
      setFiles(files);
    } catch (e) {
      console.error(e);
      navigate("/login");
    }
  }, [token, currentPath, navigate]);

  const refreshFolders = useCallback(async () => {
    if (!token) return;
    try {
      setAllFolders(await listAllFolders(token));
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    refreshFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    refreshFiles();
  }, [refreshFiles]);

  useEffect(() => {
    setSelected(new Set());
    setFocusIndex(-1);
    setLastClickIndex(null);
    setSearch("");
  }, [currentPath]);

  useEffect(() => {
    localStorage.setItem("fm-view-mode", viewMode);
  }, [viewMode]);

  /* ───────── Derived, sorted + filtered list ───────── */
  const visibleItems = useMemo(() => {
    let items = files;
    const q = search.trim().toLowerCase();
    if (q) items = items.filter((f) => f.name.toLowerCase().includes(q));

    const cmp = (a, b) => {
      let res;
      if (sortKey === "size") res = (a.size || 0) - (b.size || 0);
      else if (sortKey === "modified") res = new Date(a.modifiedAt) - new Date(b.modifiedAt);
      else res = a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? res : -res;
    };

    const dirs = items.filter((f) => f.isDirectory).sort(cmp);
    const regular = items.filter((f) => !f.isDirectory).sort(cmp);
    return [...dirs, ...regular];
  }, [files, search, sortKey, sortDir]);

  useEffect(() => {
    if (focusIndex < 0 || !containerRef.current) return;
    containerRef.current.querySelector(`[data-index="${focusIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [focusIndex]);

  /* ───────── Navigation ───────── */
  const enterDirectory = (name) => setCurrentPath((p) => (p ? `${p}/${name}` : name));
  const goUp = () => setCurrentPath((p) => p.split("/").slice(0, -1).join("/"));
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const openItem = (item) => {
    if (item.isDirectory) enterDirectory(item.name);
    else navigate(`/gallery/${currentPath}?file=${encodeURIComponent(item.name)}`);
  };

  const downloadItem = (item) => {
    const a = document.createElement("a");
    a.href = viewUrl(relPathOf(item), token);
    a.download = item.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* ───────── Selection ───────── */
  const toggleOne = (name) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const extendSelectionRange = (index) => {
    const anchor = lastClickIndex ?? index;
    const [start, end] = [anchor, index].sort((a, b) => a - b);
    setSelected(new Set(visibleItems.slice(start, end + 1).map((i) => i.name)));
  };

  // Plain click opens (friendlier for non-technical users); modifier clicks select.
  const handleRowClick = (item, index, e) => {
    setFocusIndex(index);
    if (e.shiftKey) {
      extendSelectionRange(index);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      toggleOne(item.name);
      setLastClickIndex(index);
      return;
    }
    setLastClickIndex(index);
    openItem(item);
  };

  const handleCheckboxToggle = (item, index, e) => {
    if (e.shiftKey) extendSelectionRange(index);
    else toggleOne(item.name);
    setLastClickIndex(index);
    setFocusIndex(index);
  };

  const moveFocus = (delta, extend) => {
    if (!visibleItems.length) return;
    const next = Math.min(visibleItems.length - 1, Math.max(0, (focusIndex < 0 ? 0 : focusIndex) + delta));
    setFocusIndex(next);
    if (extend) {
      const anchor = lastClickIndex ?? next;
      const [start, end] = [anchor, next].sort((a, b) => a - b);
      setSelected(new Set(visibleItems.slice(start, end + 1).map((i) => i.name)));
    } else {
      setSelected(new Set([visibleItems[next].name]));
      setLastClickIndex(next);
    }
  };

  /* ───────── Rename ───────── */
  const startRename = (item) => {
    setRenaming(item.name);
    setRenameValue(item.name);
  };
  const cancelRename = () => setRenaming(null);
  const commitRename = async () => {
    if (!renaming) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === renaming) {
      setRenaming(null);
      return;
    }
    try {
      await renameFile(token, currentPath, renaming, trimmed);
      notify.success("Renamed");
      refreshFiles();
    } catch (e) {
      notify.error(e.message || "Rename failed");
    } finally {
      setRenaming(null);
    }
  };

  /* ───────── Bulk actions ───────── */
  const handleDelete = async (names) => {
    if (!names.length) return;
    if (!window.confirm(`Move ${names.length} item(s) to Trash?`)) return;
    try {
      await bulkAction(token, currentPath, "delete", names);
      notify.success("Moved to Trash");
      setSelected(new Set());
      refreshFiles();
    } catch (e) {
      notify.error(e.message || "Delete failed");
    }
  };

  const handleMoveCopyPick = async (destination) => {
    const { mode, names } = moveCopyDialog;
    setMoveCopyDialog(null);
    try {
      if (mode === "move") await bulkAction(token, currentPath, "move", names, destination);
      else await copyFiles(token, currentPath, names, destination);
      notify.success(mode === "move" ? "Moved" : "Copied");
      setSelected(new Set());
      refreshFiles();
      refreshFolders();
    } catch (e) {
      notify.error(e.message || "Action failed");
    }
  };

  const performDrop = async (names, destination) => {
    if (destination === currentPath) return;
    if (names.some((n) => destination === relPathOf({ name: n }))) {
      notify.error("Can't move a folder into itself");
      return;
    }
    try {
      await bulkAction(token, currentPath, "move", names, destination);
      notify.success(`Moved ${names.length} item(s)`);
      setSelected(new Set());
      refreshFiles();
    } catch (e) {
      notify.error(e.message || "Move failed");
    }
  };

  /* ───────── New folder ───────── */
  const handleCreateFolder = async (name) => {
    try {
      await createFolder(token, currentPath, name);
      notify.success("Folder created");
      setShowNewFolder(false);
      refreshFiles();
      refreshFolders();
    } catch (e) {
      notify.error(e.message || "Could not create folder");
    }
  };

  /* ───────── Upload ───────── */
  const handleFilesSelected = async (fileArray) => {
    if (!fileArray?.length) return;
    setUploadProgress(0);
    try {
      await uploadFiles(token, currentPath, fileArray, setUploadProgress);
      notify.success(`Uploaded ${fileArray.length} file(s)`);
      refreshFiles();
    } catch (e) {
      notify.error(e.message || "Upload failed");
    } finally {
      setUploadProgress(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleFilesSelected,
    noClick: true,
    noKeyboard: true,
  });

  /* ───────── Drag-to-move (internal) ───────── */
  const handleDragStart = (e, item) => {
    const names = selected.has(item.name) ? [...selected] : [item.name];
    e.dataTransfer.setData(DND_MIME, JSON.stringify(names));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverTarget = (e, destPath) => {
    if (!e.dataTransfer.types.includes(DND_MIME)) return;
    e.preventDefault();
    setDropTargetPath(destPath);
  };
  const handleDragLeaveTarget = () => setDropTargetPath(null);
  const handleDropTarget = (e, destPath) => {
    if (!e.dataTransfer.types.includes(DND_MIME)) return;
    e.preventDefault();
    setDropTargetPath(null);
    const raw = e.dataTransfer.getData(DND_MIME);
    if (!raw) return;
    try {
      const names = JSON.parse(raw);
      if (names?.length) performDrop(names, destPath);
    } catch {
      // ignore malformed payloads
    }
  };

  /* ───────── Context menu ───────── */
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected((prev) => (prev.has(item.name) ? prev : new Set([item.name])));
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  const handleBackgroundContextMenu = (e) => {
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    setSelected(new Set());
    setContextMenu({ x: e.clientX, y: e.clientY, item: null });
  };

  const buildContextMenuItems = (item) => {
    if (!item) {
      return [
        { label: "➕ New Folder", onClick: () => setShowNewFolder(true) },
        { label: "⬆️ Upload Files…", onClick: open },
      ];
    }
    const names = selected.size > 0 ? [...selected] : [item.name];
    const items = [{ label: item.isDirectory ? "📂 Open" : "👁️ View", onClick: () => openItem(item) }];
    if (!item.isDirectory) items.push({ label: "⬇️ Download", onClick: () => downloadItem(item) });
    if (names.length === 1) items.push({ label: "✏️ Rename", onClick: () => startRename(item) });
    items.push({ separator: true });
    items.push({ label: "📁 Move to…", onClick: () => setMoveCopyDialog({ mode: "move", names }) });
    items.push({ label: "📋 Copy to…", onClick: () => setMoveCopyDialog({ mode: "copy", names }) });
    items.push({ separator: true });
    items.push({
      label: `🗑️ Delete${names.length > 1 ? ` (${names.length} items)` : ""}`,
      danger: true,
      onClick: () => handleDelete(names),
    });
    return items;
  };

  /* ───────── Keyboard shortcuts ───────── */
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || renaming) return;
      if (contextMenu || showNewFolder || moveCopyDialog || showTrash) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
        e.preventDefault();
        setSelected(new Set(visibleItems.map((i) => i.name)));
        return;
      }
      if (e.key === "Escape") {
        setSelected(new Set());
        return;
      }
      if (e.key === "Delete") {
        if (selected.size) {
          e.preventDefault();
          handleDelete([...selected]);
        }
        return;
      }
      if (e.key === "Backspace") {
        if (currentPath) {
          e.preventDefault();
          goUp();
        }
        return;
      }
      if (e.key === "F2") {
        if (selected.size === 1) {
          const name = [...selected][0];
          const item = visibleItems.find((i) => i.name === name);
          if (item) startRename(item);
        }
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        moveFocus(1, e.shiftKey);
        return;
      }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        moveFocus(-1, e.shiftKey);
        return;
      }
      if (e.key === "Enter") {
        if (focusIndex >= 0 && visibleItems[focusIndex]) openItem(visibleItems[focusIndex]);
        return;
      }
      if (e.key === " ") {
        if (focusIndex >= 0 && visibleItems[focusIndex]) {
          e.preventDefault();
          toggleOne(visibleItems[focusIndex].name);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleItems, selected, focusIndex, renaming, contextMenu, showNewFolder, moveCopyDialog, showTrash, lastClickIndex, currentPath]);

  const breadcrumbSegments = [""].concat(currentPath ? currentPath.split("/") : []);

  return (
    <div className="main-container fm-page">
      <header className="fm-header">
        <div className="fm-brand">
          <img src="/logoFAM.png" alt="" className="fm-logo" />
          <h1>Teare Family Files</h1>
        </div>
        <div className="fm-header-actions">
          {username && <span className="fm-username">👋 {username}</span>}
          <button className="fm-btn fm-btn-ghost" onClick={handleLogout}>Log out</button>
        </div>
      </header>

      <nav className="breadcrumbs">
        {breadcrumbSegments.map((seg, i, arr) => {
          const segPath = arr.slice(1, i + 1).join("/");
          return (
            <React.Fragment key={i}>
              {i > 0 && <span className="crumb-sep">/</span>}
              <button
                className={`crumb ${dropTargetPath === segPath ? "drop-target" : ""}`}
                disabled={i === arr.length - 1}
                onClick={() => setCurrentPath(segPath)}
                onDragOver={(e) => handleDragOverTarget(e, segPath)}
                onDragLeave={handleDragLeaveTarget}
                onDrop={(e) => handleDropTarget(e, segPath)}
              >
                {seg || "🏠 Home"}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortChange={(key, dir) => {
          setSortKey(key);
          setSortDir(dir);
        }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNewFolder={() => setShowNewFolder(true)}
        onUploadClick={open}
        onTrashClick={() => setShowTrash(true)}
      />

      {uploadProgress !== null && (
        <div className="upload-progress-track">
          <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
          <span className="upload-progress-label">Uploading… {uploadProgress}%</span>
        </div>
      )}

      {selected.size > 0 && (
        <SelectionBar
          count={selected.size}
          onMove={() => setMoveCopyDialog({ mode: "move", names: [...selected] })}
          onCopy={() => setMoveCopyDialog({ mode: "copy", names: [...selected] })}
          onDelete={() => handleDelete([...selected])}
          onClear={() => setSelected(new Set())}
        />
      )}

      <div
        {...getRootProps()}
        ref={containerRef}
        className={`file-collection ${viewMode}`}
        onContextMenu={handleBackgroundContextMenu}
      >
        <input {...getInputProps()} />
        {isDragActive && <div className="dropzone-overlay">Drop files to upload…</div>}

        {visibleItems.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">{search ? "🔍" : "📂"}</span>
            <p>{search ? `Nothing here matches “${search}”` : "This folder is empty"}</p>
            {!search && <p className="empty-hint">Drag photos here, or press the Upload button above</p>}
          </div>
        )}

        {visibleItems.map((item, i) => (
          <FileItem
            key={item.name}
            item={item}
            index={i}
            filePath={relPathOf(item)}
            token={token}
            viewMode={viewMode}
            selected={selected.has(item.name)}
            focused={focusIndex === i}
            isRenaming={renaming === item.name}
            renameValue={renameValue}
            onRenameChange={setRenameValue}
            onRenameCommit={commitRename}
            onRenameCancel={cancelRename}
            onToggleSelect={(e) => handleCheckboxToggle(item, i, e)}
            onClick={(e) => handleRowClick(item, i, e)}
            onOpen={() => openItem(item)}
            onContextMenu={(e) => handleContextMenu(e, item)}
            draggable={renaming !== item.name}
            onDragStart={(e) => handleDragStart(e, item)}
            onDragOver={item.isDirectory ? (e) => handleDragOverTarget(e, relPathOf(item)) : undefined}
            onDragLeave={item.isDirectory ? handleDragLeaveTarget : undefined}
            onDrop={item.isDirectory ? (e) => handleDropTarget(e, relPathOf(item)) : undefined}
            isDropTarget={item.isDirectory && dropTargetPath === relPathOf(item)}
          />
        ))}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={buildContextMenuItems(contextMenu.item)}
          onClose={() => setContextMenu(null)}
        />
      )}
      {showNewFolder && (
        <NewFolderDialog onClose={() => setShowNewFolder(false)} onCreate={handleCreateFolder} />
      )}
      {moveCopyDialog && (
        <MoveCopyDialog
          mode={moveCopyDialog.mode}
          count={moveCopyDialog.names.length}
          folders={allFolders}
          excludePath={currentPath}
          onClose={() => setMoveCopyDialog(null)}
          onPick={handleMoveCopyPick}
        />
      )}
      {showTrash && (
        <TrashPanel token={token} onClose={() => setShowTrash(false)} onChanged={refreshFiles} />
      )}
    </div>
  );
}

export default FileManager;
